<script lang="ts" setup>
import type { Group, GroupMember } from '~/models/Group'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { Settings } from 'luxon'
import { faCopy, faPenToSquare, faTrash } from '@fortawesome/duotone-regular-svg-icons'
import { AuToast } from '~/stores/toasts'
import useGroupsStore from '~/stores/groups'
import { UserRole } from '~/models/User/UserRole'
import { GroupRole } from '~/models/Group'
import Button from '~/components/ui/Button.vue'
import AuSpinner from '~/components/ui/AuSpinner.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import AuAccordionItem from '~/components/ui/AuAccordionItem.vue'
import AuAccordion from '~/components/ui/AuAccordion.vue'

const props = defineProps<{
  group: Group
  groupMembers: GroupMember[]
}>()

const emit = defineEmits(['deleteGroup', 'changeView', 'copyGroup'])

const { t } = useI18n()
const groupsStore = useGroupsStore()
const { isAdminInGroup, removeGroup, sortMembers } = groupsStore
const { isLoading } = storeToRefs(groupsStore)

const deleteDialogOpen = ref(false)
const disjunction = new Intl.ListFormat(Settings.defaultLocale, { type: 'disjunction' })

const isAdmin = computed(() => isAdminInGroup(props.group))
const membersTitle = computed(() => t('groups.membersCount', { count: props.groupMembers.length }))

const groupMembersSorted = computed(() => {
  const teachers = props.groupMembers.filter(({ role }) => role === UserRole.Teacher).sort(sortMembers)
  const students = props.groupMembers.filter(({ role }) => role !== UserRole.Teacher).sort(sortMembers)
  return [teachers, students].flatMap(member => member)
})

const adminNames = computed(() => disjunction.format(props.groupMembers
  .filter(({ groupRole }) => groupRole === GroupRole.Admin)
  .map(({ firstName }) => firstName))
)
const nonAdminText = computed(() => adminNames.value.length > 0
  ? t('groups.nonAdminContact', { admins: adminNames.value })
  : t('groups.nonAdminGroup')
)

async function deleteGroup() {
  const { group } = props
  try {
    await removeGroup(group)
    AuToast.success(t('groups.groupDeleted'))
  } catch (error) {
    AuToast.error(t('groups.groupDeleteError'))
    throw error
  } finally {
    emit('deleteGroup')
  }
}
</script>

<template>
  <div class="animate-fade flex flex-col gap-6 py-6">
    <AuCallout
      v-if="!isAdmin"
      class="pt-6"
      variant="info"
      :title="nonAdminText"
    />
    <h2
      class="text-xl font-medium"
      v-text="props.group.name"
    />
    <div
      v-if="isLoading"
      class="flex justify-center"
    >
      <AuSpinner />
    </div>
    <AuAccordion v-if="!isLoading" collapsible>
      <AuAccordionItem
        :title="membersTitle"
        :value="`accordion-${props.group.groupId}`"
        size="lg"
      >
        <ul class="space-y-2">
          <li
            v-for="member in groupMembersSorted"
            :key="`${props.group.groupId}-${member.userId}`"
            class="font-medium"
          >
            <p class="flex items-center gap-3">
              {{ member.fullName }}
              <span
                v-if="member.groupRole === GroupRole.Admin"
                class="text-coral-60 rounded-sm bg-yellow-50 px-2 py-0.5 text-xs font-bold tracking-widest uppercase"
                v-text="t('groups.admin')"
              />
            </p>
          </li>
        </ul>
      </AuAccordionItem>
    </AuAccordion>
    <div
      v-if="!isLoading"
      class="mt-2 flex flex-wrap items-center gap-4"
    >
      <Button
        :disabled="!isAdmin"
        shade="light"
        size="lg"
        variant="outline"
        @click="emit('changeView')"
      >
        <font-awesome-icon :icon="faPenToSquare" />
        {{ t('groups.editGroup') }}
      </Button>
      <Button
        shade="light"
        size="lg"
        variant="outline"
        @click="emit('copyGroup')"
      >
        <font-awesome-icon :icon="faCopy" />
        {{ t('groups.copyGroup') }}
      </Button>

      <AuDialog
        v-model:open="deleteDialogOpen"
        :description="t('groups.groupDeleteConfirm')"
        :title="t('groups.deleteGroup')"
      >
        <template #trigger>
          <Button
            :disabled="!isAdmin"
            shade="light"
            size="lg"
            variant="ghost"
          >
            <font-awesome-icon :icon="faTrash" />
            {{ t('groups.deleteGroup') }}
          </Button>
        </template>
        <template #content>
          <div class="grid grid-cols-2 gap-3">
            <Button
              center
              full-width
              size="lg"
              variant="outline"
              @click="deleteDialogOpen = false"
            >
              {{ t('groups.cancel') }}
            </Button>
            <Button
              center
              color="coral"
              full-width
              size="lg"
              @click.prevent="deleteGroup"
            >
              {{ t('groups.deleteGroup') }}
            </Button>
          </div>
        </template>
      </AuDialog>
    </div>
  </div>
</template>
